<script setup lang="ts">
import { inject, computed } from 'vue'
import { GeoImagesViewModelKey } from '../keys/GeoImagesViewModel.key'
import InputText from '../../../../components/InputText/InputText.vue'
import DropdownFk from '../../../../components/DropdownFk/DropdownFk.vue'
import DatePicker from '../../../../components/DatePicker/DatePicker.vue'
import RadioButtons from '../../../../components/RadioButtons/RadioButtons.vue'
import NewFileUpload from '../../../../components/NewFileUpload/NewFileUpload.vue'
import Button from '../../../../components/Button/Button.vue'
import HelpTooltip from '../../../ui/components/HelpTooltip.vue'

const vm = inject(GeoImagesViewModelKey)!

const farmDropdownOptions = computed(() => {
	return vm.farmOptions.value.map((farm) => ({
		id: farm.id,
		Código: farm.code,
		Nome: farm.name,
	}))
})

const selectedFarmId = computed({
	get: () => vm.model.value.farm.id,
	set: (id: string) => {
		const farm = vm.farmOptions.value.find((f) => f.id === id)
		if (farm) {
			vm.model.value.farm = { name: farm.name, id: farm.id! }
		} else {
			vm.model.value.farm = { name: '', id: '' }
		}
	},
})

const acquisitionDateIso = computed({
	get: () => vm.model.value.acquisitionDate?.toISOString() ?? '',
	set: (isoString: string) => {
		vm.model.value.acquisitionDate = isoString ? new Date(isoString) : null
	},
})

const maxAcquisitionDateIso = new Date().toISOString()

const formTitle = computed(() =>
	vm.model.value.id ? 'Editar Cadastro de Imagem' : 'Cadastrar Imagem',
)

const uploadedFiles = computed(() => (vm.model.value.image ? [vm.model.value.image] : []))

vm.fetchFarms()
</script>

<template>
	<div>
		<h2 class="text-H5 font-bold mb-2">{{ formTitle }}</h2>

		<p class="text-sm text-gray-600 mb-4">
			Faça upload de imagens de satélite, drone, LiDAR ou topográficas e utilize no processamento de
			variáveis auxiliares
		</p>

		<div style="display: flex; gap: 0.5rem; flex-direction: column">
			<InputText
				v-model="vm.model.value.name"
				label="Nome*"
			>
				<HelpTooltip
					title="Nome"
					description="Atribua um nome a imagem cadastrada. Esse nome irá te guiar no momento da seleção de imagens no processamento."
				/>
			</InputText>
			<InputText
				v-model="vm.model.value.description"
				label="Descrição"
			>
				<HelpTooltip
					title="Descrição"
					description="Insira uma descrição que defina de forma mais completa o que é a imagem cadastrada ou qualquer outro detalhamento necessário"
				/>
			</InputText>
			<DropdownFk
				v-model="selectedFarmId"
				label="Fazenda*"
				:dropdown-options="farmDropdownOptions"
			>
				<HelpTooltip
					title="Fazenda"
					description="Defina a qual fazenda a imagem está atrelada."
				/>
			</DropdownFk>
			<DatePicker
				v-model="acquisitionDateIso"
				label="Data de Aquisição*"
				:max-date="maxAcquisitionDateIso"
			>
				<HelpTooltip
					title="Data de aquisição"
					description="Quando a imagem foi gerada ou adquirida?"
				/>
			</DatePicker>
			<RadioButtons
				v-model="vm.model.value.imageType"
				group-name="imageType"
				label="Tipo de Imagem*"
				:options="vm.imageTypeOptions.value"
			>
				<HelpTooltip
					title="Tipo de imagem"
					description="Defina qual o tipo de imagem que está sendo carregada: Drone, LiDAR, Satélite ou MDT (Modelo Digital de Terreno)."
				/>
			</RadioButtons>
			<NewFileUpload
				accept=".tif,.geotiff"
				:multiple="false"
				:max-file-size="100"
				:max-files="1"
				:files="uploadedFiles"
				file-size-message="Até 200MB por imagem."
				@onInput="vm.handleFileUpload($event)"
			/>

			<div v-if="vm.model.value.bandCount">
				<div style="display: flex; gap: 0.25rem; align-items: center">
					<label class="text-label">tamanho do pixel</label>
					<HelpTooltip
						title="Tamanho do pixel"
						description="O campo é calculado de forma automática utilizando a imagem."
					/>
				</div>
				<p class="text-s">{{ vm.model.value.pixel }} m</p>
			</div>
			<div v-if="vm.model.value.pixel">
				<div style="display: flex; gap: 0.25rem; align-items: center">
					<label class="text-label">número de camadas</label>
					<HelpTooltip
						title="Número de camadas"
						description="O campo é calculado de forma automática utilizando a imagem."
					/>
				</div>
				<p class="text-s">{{ vm.model.value.bandCount }}</p>
			</div>
		</div>

		<div class="flex gap-4 mt-4">
			<Button
				label="Cancelar"
				color="trv-primary"
				semantic-class="mini"
				outlined
				@click="vm.changeFormAndCloseAllActions()"
			/>
			<Button
				v-if="vm.requiresBandDefinition.value"
				label="Avançar"
				color="trv-default"
				semantic-class="mini"
				:disabled="vm.disableSaveButton.value || !vm.camposObrigatoriosPreenchidos.value"
				@click="vm.advanceToStep1()"
			/>
			<Button
				v-else
				:label="vm.isEdit.value ? 'Salvar' : 'Avançar'"
				color="trv-default"
				semantic-class="mini"
				:disabled="vm.disableSaveButton.value || !vm.camposObrigatoriosPreenchidos.value"
				:loading="vm.disableSaveButton.value"
				@click="vm.saveClick()"
			/>
		</div>
	</div>
</template>
